import { apiProblemDetailsSchema } from "@broadview/contracts";
import { clientError, errorFromProblem, type BroadviewClientError } from "./errors";

export type BroadviewFetch = typeof fetch;

export type LiveTransportOptions = Readonly<{
  apiOrigin: string;
  getAccessToken: () => Promise<string | undefined>;
  fetchImpl?: BroadviewFetch | undefined;
}>;

export type TenantRequest = Readonly<{
  path: string;
  tenantId?: string;
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  idempotencyKey?: string;
}>;

function normalizeApiOrigin(apiOrigin: string): string {
  let url: URL;
  try {
    url = new URL(apiOrigin);
  } catch {
    throw clientError(
      503,
      "LIVE_ADAPTER_DISABLED",
      "Live API mode requires a valid HTTPS API origin.",
    );
  }

  if (url.protocol !== "https:" || url.pathname !== "/" || url.search || url.hash) {
    throw clientError(
      503,
      "LIVE_ADAPTER_DISABLED",
      "Live API mode requires an HTTPS origin without a path, query, or fragment.",
    );
  }

  return url.origin;
}

function requestId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `broadview-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

async function responsePayload(response: Response): Promise<unknown | undefined> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json") && !contentType.includes("+json")) return undefined;

  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

function responseError(response: Response, payload: unknown | undefined): BroadviewClientError {
  if (payload) {
    const parsed = apiProblemDetailsSchema.safeParse(payload);
    if (parsed.success) return errorFromProblem(parsed.data);
  }

  const correlationId = response.headers.get("x-correlation-id") ?? undefined;
  return clientError(
    response.status || 503,
    "UNAVAILABLE",
    "Broadview API returned an unexpected error response.",
    { correlationId, retryable: response.status >= 500 || response.status === 429 },
  );
}

/**
 * Thin authenticated `/api/v1` transport. It carries a token supplied by the
 * host app but never creates, stores, refreshes, or interprets identity/role claims.
 */
export function createLiveTransport(options: LiveTransportOptions) {
  const apiOrigin = normalizeApiOrigin(options.apiOrigin);
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;

  return async function getJson<T>(
    request: TenantRequest,
    parse: (payload: unknown, correlationId?: string) => T,
  ): Promise<T> {
    const token = await options.getAccessToken();
    if (!token) {
      throw clientError(401, "UNAUTHENTICATED", "A verified Broadview session is required.");
    }

    const headers = new Headers({
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "X-Correlation-Id": requestId(),
    });
    if (request.tenantId) headers.set("X-Tenant-Id", request.tenantId);
    if (request.idempotencyKey) headers.set("Idempotency-Key", request.idempotencyKey);
    if (request.body !== undefined) headers.set("Content-Type", "application/json");

    let response: Response;
    try {
      response = await fetchImpl(`${apiOrigin}/api/v1${request.path}`, {
        headers,
        method: request.method ?? "GET",
        ...(request.body !== undefined ? { body: JSON.stringify(request.body) } : {}),
      });
    } catch {
      throw clientError(
        503,
        "UNAVAILABLE",
        "Broadview API is unavailable. Fixture data was not substituted.",
        {
          retryable: true,
        },
      );
    }

    const payload = await responsePayload(response);
    if (!response.ok) throw responseError(response, payload);

    const correlationId = response.headers.get("x-correlation-id") ?? undefined;
    try {
      return parse(payload, correlationId);
    } catch (error) {
      if (error instanceof Error && error.name === "BroadviewClientError") throw error;
      throw clientError(
        502,
        "CONTRACT_MISMATCH",
        "Broadview API returned a response that does not match the approved frontend contract.",
        { correlationId },
      );
    }
  };
}
